import React, { useMemo } from 'react';
import { GetEmailReceipt, IsReceiptReady } from 'api/taxiTrip';

import './TripDetail.scss';

import Ripple from 'components/Ripple';
import { Button } from 'components/Button/Button';

import { useTranslation } from 'react-i18next';

import { TransformedTrip, TripStatus } from 'types/trip';
import { TRIP_PAYMENT_METHOD } from 'constants/index';
import { useReceiptModal } from 'components/ReceiptModal';
import { setToastAppear } from 'components/Toast/Toast';

interface TripDetailProps {
  tripDetail: TransformedTrip;
  onClick?: () => void;
  userEmail?: string;
}

// 可以索取收據的行程狀態
const RECEIPT_AVAILABLE_STATUSES = ['已付款', '已部分退款'];

export const TripDetail: React.FC<TripDetailProps> = ({ tripDetail, onClick, userEmail }) => {
  const { t } = useTranslation();

  const canClick = !!onClick;
  const isOnlinePay = tripDetail.payMethod === TRIP_PAYMENT_METHOD.CREDIT_CARD;
  const isCompleted = tripDetail?.tripStatus?.status === TripStatus.Completed;

  // 使用 useMemo 計算是否可以索取收據
  const canGetReceipt = useMemo(() => {
    return tripDetail && RECEIPT_AVAILABLE_STATUSES.includes(tripDetail.tripStatusDesc);
  }, [tripDetail]);

  function showDispatchPlate(history) {
    return history.dispatchPlate && history.tripStatus.status !== TripStatus.Cancelled;
  }

  const { ReceiptModal, toggleReceiptModal } = useReceiptModal({
    defaultEmail: userEmail,
    onConfirm: async (email: string) => {
      const [res, error] = await GetEmailReceipt({ tripGuid: tripDetail.guid, email });
      // 202: 已建立發送排程
      if (res?.status === 202) {
        setToastAppear('發送成功！請稍候至您的電子信箱收取');
      } else {
        setToastAppear('發送失敗');
      }
      if (error) {
        setToastAppear('發送失敗');
      }
    },
  });

  async function handleReceiptModal() {
    if (!canGetReceipt) {
      setToastAppear('無法索取車資收據');
      return;
    }
    const [res, error] = await IsReceiptReady({ tripGuid: tripDetail.guid });
    if (error) {
      setToastAppear('無法索取車資收據');
      return;
    }
    if (res.data.startsWith('收據尚未開立')) {
      setToastAppear('收據尚未開立，請等待1小時後再試');
      return;
    }
    toggleReceiptModal();
  }

  if (!tripDetail) return null;

  return (
    <div className="history history-detail-block" onClick={onClick}>
      {canClick && <Ripple />}
      <div className="history__left">
        <div className="history__left--header">
          <span>{tripDetail.origin.formattedTime}</span>

          {tripDetail.comment && (
            <div className="history__left__star history__left__item">
              <i className="icon-star-full" aria-hidden="true" />
              <p>{tripDetail.comment}</p>
            </div>
          )}
        </div>
        <div className="history__left--body">
          {isOnlinePay ? (
            <div className="history__left__pay">
              <span className="amount">${tripDetail.receipt.balance}</span>
              {tripDetail.receipt.balance > 0 && (
                <>
                  <span className="divided">|</span>
                  <span className="method">{tripDetail.payMethod}</span>
                </>
              )}
            </div>
          ) : (
            <>{isCompleted && <span className="method">{tripDetail.payMethod}</span>}</>
          )}
          {showDispatchPlate(tripDetail) && (
            <div className="history__left__car-info">
              <div className="history__left__car-info__license">
                <span>車牌 {tripDetail.dispatchPlate}</span>
              </div>
            </div>
          )}
          <div className="history__left__address">
            <i className="icon-car" aria-hidden="true" />
            <span>{tripDetail.origin.address}</span>
          </div>
          <div className="history__left__down">
            <i className="icon-down" aria-hidden="true" />
          </div>
          <div className="history__left__location">
            <i className="icon-marker" aria-hidden="true" />
            <span>
              {tripDetail.destination && tripDetail.destination.address
                ? tripDetail.destination.address
                : t('A069')}
            </span>
          </div>
        </div>
      </div>
      <div className="history__right">
        <div className="history__right--header">
          <span className={`${tripDetail.tripStatus.color} history__right__status`}>
            {tripDetail.tripStatus.text}
          </span>
        </div>
        {canClick && (
          <div className="history__right--body">
            <div className="icon-container">
              <i className="icon-arrow-right"></i>
            </div>
          </div>
        )}
        {canGetReceipt && (
          <Button
            className="history__right__receipt-button"
            color="primary"
            style={{ borderRadius: '18px' }}
            variant="outlined"
            height="36px"
            onButtonClick={(e) => {
              e.stopPropagation();
              handleReceiptModal();
            }}
          >
            <span>車資收據</span>
          </Button>
        )}
      </div>
      <div className="receipt-modal-container" onClick={(e) => e.stopPropagation()}>
        {ReceiptModal}
      </div>
    </div>
  );
};
